import type {GroupableTicket} from '../types/groupableTicket';
import {STATUS_ORDER, isResolvedStatus} from '../../tickets/constants/statuses';
import {PRIORITY_ORDER, PRIORITY_LABELS} from '../../tickets/constants/priority';
import {CHANNEL_LABELS} from '../../tickets/constants/channels';
import {STATUS_LABELS} from '../../tickets/components/TicketStatusBadge';
import {medianResolutionSeconds} from './medianResolution';

export type DashboardGroupByToken = 'none' | 'status' | 'priority' | 'assignee' | 'channel' | 'project' | 'requester';

export interface TicketGroupStats {
	count: number;
	/**
	 * Share of the group that has reached resolved or closed, as a ratio in 0–1.
	 * Null when the group is empty.
	 *
	 * A ratio rather than a rounded percentage so it matches
	 * `DashboardTotals.pct_resolved` and can be rendered by the same formatter as
	 * the totals row — a group subtotal and the table's totals must round once, in
	 * the same place, or the two disagree on the same rows.
	 */
	resolvedRatio: number | null;
	/** Median resolution time across the group's resolved tickets, in seconds. Null when none are. */
	medianResolutionSeconds: number | null;
}

export interface TicketGroup<T extends GroupableTicket = GroupableTicket> {
	/** Stable identity for the bucket — an enum value or an entity id. */
	key: string;
	/**
	 * The key made safe to embed in an `id` attribute, and unique within the
	 * result. Group keys fall back to display names when a shape carries no ids,
	 * and a name contains spaces — which would silently break `aria-controls`,
	 * since that attribute is a space-separated list of ids rather than one id.
	 */
	domKey: string;
	label: string;
	tickets: T[];
	stats: TicketGroupStats;
}

/** Sentinel keys for the buckets that stand for an absent relation. */
export const UNASSIGNED_KEY = '__unassigned__';
export const NO_PROJECT_KEY = '__no_project__';
export const UNKNOWN_REQUESTER_KEY = '__unknown_requester__';
export const ALL_KEY = '__all__';

function buildStats(tickets: readonly GroupableTicket[]): TicketGroupStats {
	if (tickets.length === 0) {
		return {count: 0, resolvedRatio: null, medianResolutionSeconds: null};
	}
	const resolved = tickets.filter(t => isResolvedStatus(t.status)).length;
	return {
		count: tickets.length,
		resolvedRatio: resolved / tickets.length,
		medianResolutionSeconds: medianResolutionSeconds(tickets),
	};
}

/** Buckets in insertion order, keyed by identity, carrying a label and its rows. */
type Buckets<T> = Map<string, {label: string; tickets: T[]}>;

function push<T>(buckets: Buckets<T>, key: string, label: string, ticket: T): void {
	const existing = buckets.get(key);
	if (existing) {
		existing.tickets.push(ticket);
	} else {
		buckets.set(key, {label, tickets: [ticket]});
	}
}

/**
 * Turns buckets into groups, assigning each a unique `domKey`. A sanitized key can
 * collide with another (two requester names differing only in punctuation), so a
 * counter disambiguates rather than letting two group bodies share an id.
 */
function toGroups<T extends GroupableTicket>(buckets: Buckets<T>): TicketGroup<T>[] {
	const used = new Set<string>();
	return Array.from(buckets.entries()).map(([key, {label, tickets}]) => {
		const base = key.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'group';
		let domKey = base;
		for (let n = 2; used.has(domKey); n++) {
			domKey = `${base}-${n}`;
		}
		used.add(domKey);
		return {key, domKey, label, tickets, stats: buildStats(tickets)};
	});
}

/** Orders buckets by a canonical enum order, dropping the ones with no tickets. */
function orderedByEnum<T extends GroupableTicket, V extends string>(buckets: Buckets<T>, order: readonly V[]): Buckets<T> {
	const ordered: Buckets<T> = new Map();
	for (const value of order) {
		const bucket = buckets.get(value);
		if (bucket) ordered.set(value, bucket);
	}
	// Anything outside the canonical order (a workspace-custom status) still shows,
	// after the known values, rather than vanishing from the grouped view.
	for (const [key, bucket] of buckets) {
		if (!ordered.has(key)) ordered.set(key, bucket);
	}
	return ordered;
}

/**
 * Orders buckets alphabetically by label, keeping the "absent relation" bucket
 * last — Unassigned / No project reads as the tail of a list, not as a name
 * sorted under U or N.
 */
function orderedByLabel<T extends GroupableTicket>(buckets: Buckets<T>, sentinelKey: string): Buckets<T> {
	const entries = Array.from(buckets.entries()).sort(([keyA, a], [keyB, b]) => {
		if (keyA === sentinelKey) return 1;
		if (keyB === sentinelKey) return -1;
		return a.label.localeCompare(b.label);
	});
	return new Map(entries);
}

function groupByStatus<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		push(buckets, ticket.status, STATUS_LABELS[ticket.status] ?? ticket.status, ticket);
	}
	return toGroups(orderedByEnum(buckets, STATUS_ORDER));
}

function groupByPriority<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		push(buckets, ticket.priority, PRIORITY_LABELS[ticket.priority] ?? ticket.priority, ticket);
	}
	return toGroups(orderedByEnum(buckets, PRIORITY_ORDER));
}

function groupByChannel<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		push(buckets, ticket.channel, CHANNEL_LABELS[ticket.channel] ?? ticket.channel, ticket);
	}
	return toGroups(orderedByLabel(buckets, ''));
}

function groupByAssignee<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		// A shape with no `assignee_id` is not therefore unassigned — it identifies
		// its assignee by name. Only the absence of BOTH means nobody owns the ticket.
		const id = ticket.assignee_id ?? null;
		const name = ticket.assignee_name ?? null;
		if (id === null && name === null) {
			push(buckets, UNASSIGNED_KEY, 'Unassigned', ticket);
		} else {
			push(buckets, id ?? name!, name ?? id!, ticket);
		}
	}
	return toGroups(orderedByLabel(buckets, UNASSIGNED_KEY));
}

function groupByRequester<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		const id = ticket.requester_id ?? null;
		const name = ticket.requester_name ?? null;
		const email = ticket.requester_email ?? null;
		if (id === null && name === null && email === null) {
			push(buckets, UNKNOWN_REQUESTER_KEY, 'Unknown requester', ticket);
		} else {
			push(buckets, id ?? name ?? email!, name ?? email ?? id!, ticket);
		}
	}
	return toGroups(orderedByLabel(buckets, UNKNOWN_REQUESTER_KEY));
}

function groupByProject<T extends GroupableTicket>(tickets: readonly T[]): TicketGroup<T>[] {
	const buckets: Buckets<T> = new Map();
	for (const ticket of tickets) {
		// A shape with no project relation at all buckets every ticket under
		// "No project" — which is the truth for it, not a stub.
		const project = ticket.project ?? null;
		if (project === null) {
			push(buckets, NO_PROJECT_KEY, 'No project', ticket);
		} else {
			push(buckets, project.id, project.name, ticket);
		}
	}
	return toGroups(orderedByLabel(buckets, NO_PROJECT_KEY));
}

/**
 * Buckets `tickets` by the chosen token, purely client-side — grouping never
 * re-fetches, so it re-lays-out the rows already on screen (PRD §13).
 *
 * Returns an empty array for an empty input. Under `'none'` returns the single
 * `ALL_KEY` group holding every ticket, so a caller can render one code path for
 * every token and let the ungrouped case fall out of it. The input array and its
 * objects are never mutated.
 */
export function groupTickets<T extends GroupableTicket>(tickets: readonly T[], token: DashboardGroupByToken): TicketGroup<T>[] {
	if (tickets.length === 0) return [];

	switch (token) {
		case 'none':
			return toGroups(new Map([[ALL_KEY, {label: 'All', tickets: [...tickets]}]]));
		case 'status':
			return groupByStatus(tickets);
		case 'priority':
			return groupByPriority(tickets);
		case 'assignee':
			return groupByAssignee(tickets);
		case 'channel':
			return groupByChannel(tickets);
		case 'project':
			return groupByProject(tickets);
		case 'requester':
			return groupByRequester(tickets);
	}
}

/** The group-by menu, in the order PRD §6.7 lists it. "No grouping" is the default. */
export const GROUP_BY_OPTIONS: ReadonlyArray<{id: DashboardGroupByToken; label: string}> = [
	{id: 'none', label: 'No grouping'},
	{id: 'status', label: 'Status'},
	{id: 'priority', label: 'Priority'},
	{id: 'assignee', label: 'Assignee'},
	{id: 'channel', label: 'Channel'},
	{id: 'project', label: 'Project'},
	{id: 'requester', label: 'Requester'},
];
